import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
    Play,
    Lock,
    CheckCircle,
    GraduationCap,
    FileText,
    Users,
    Sparkles,
    ChevronRight,
    Clock,
    Video,
    Loader2
} from 'lucide-react';

// Icon mapping for modules
const moduleIcons = {
    'Getting Started': Sparkles,
    'Application Process': FileText,
    'Interview Preparation': Users,
    'Training & Beyond': GraduationCap
};

export default function CourseVideos() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [activeVideo, setActiveVideo] = useState(null);
    const [completedVideos, setCompletedVideos] = useState([]);
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadVideos();
        // Load completed videos from localStorage
        const saved = localStorage.getItem('completedCourseVideos');
        if (saved) {
            setCompletedVideos(JSON.parse(saved));
        }
    }, []);

    const loadVideos = async () => {
        try {
            const res = await api.get('/course/videos');
            setVideos(res.data);
        } catch (err) {
            console.error('Failed to load videos:', err);
        } finally {
            setLoading(false);
        }
    };

    // Group videos by module
    const groupedVideos = videos.reduce((acc, video) => {
        const module = video.module || 'Other';
        if (!acc[module]) {
            acc[module] = [];
        }
        acc[module].push(video);
        return acc;
    }, {});

    // Convert to array format for rendering
    const courseModules = Object.entries(groupedVideos).map(([moduleName, moduleVideos], index) => ({
        id: index + 1,
        title: moduleName,
        description: `${moduleVideos.length} video${moduleVideos.length > 1 ? 's' : ''}`,
        icon: moduleIcons[moduleName] || GraduationCap,
        videos: moduleVideos.map(v => ({
            id: v.youtube_id,
            title: v.title,
            description: v.description,
            duration: v.duration
        }))
    }));

    const markAsComplete = (videoId) => {
        const updated = [...completedVideos, videoId];
        setCompletedVideos(updated);
        localStorage.setItem('completedCourseVideos', JSON.stringify(updated));
    };

    const isVideoComplete = (videoId) => completedVideos.includes(videoId);

    const totalVideos = courseModules.reduce((acc, m) => acc + m.videos.length, 0);
    const completedCount = completedVideos.length;
    const progressPercent = totalVideos > 0 ? Math.round((completedCount / totalVideos) * 100) : 0;

    // Show loading
    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    // Check if user has course access
    if (!user?.has_course_access) {
        return (
            <div className="min-h-screen">
                <section className="py-16">
                    <div className="container mx-auto px-4">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="max-w-2xl mx-auto text-center"
                        >
                            <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
                                <Lock className="h-10 w-10 text-muted-foreground" />
                            </div>
                            <h1 className="font-heading text-3xl font-bold mb-4">
                                Course Access Required
                            </h1>
                            <p className="text-muted-foreground mb-8">
                                Purchase the Master Courses to unlock all 7 professional video classes 
                                and start your journey to becoming a flight attendant.
                            </p>
                            <Button 
                                size="lg"
                                className="rounded-full gap-2"
                                onClick={() => navigate('/course')}
                                data-testid="purchase-course-btn"
                            >
                                <GraduationCap className="h-5 w-5" />
                                Get Master Courses - $250
                            </Button>
                        </motion.div>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <section className="py-8 border-b">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <Badge variant="secondary" className="mb-2">
                                <Video className="h-3 w-3 mr-1" />
                                Master Courses
                            </Badge>
                            <h1 className="font-heading text-2xl md:text-3xl font-bold">
                                Your Video Classes
                            </h1>
                            <p className="text-muted-foreground mt-1">
                                {totalVideos} classes • Watch at your own pace
                            </p>
                        </div>
                        
                        {/* Progress */}
                        <Card className="p-4 min-w-[200px]">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium">Progress</span>
                                <span className="text-sm text-muted-foreground">{completedCount}/{totalVideos}</span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-primary rounded-full transition-all duration-500"
                                    style={{ width: `${progressPercent}%` }}
                                />
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                                {progressPercent}% complete
                            </p>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Video Player Section */}
            {activeVideo && (
                <section className="bg-[#1a1a2e] py-8">
                    <div className="container mx-auto px-4">
                        <div className="max-w-4xl mx-auto">
                            <div className="aspect-video rounded-lg overflow-hidden bg-black">
                                <iframe
                                    width="100%"
                                    height="100%"
                                    src={`https://www.youtube.com/embed/${activeVideo.id}?autoplay=1&rel=0`}
                                    title={activeVideo.title}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    className="w-full h-full"
                                />
                            </div>
                            <div className="mt-4 flex items-center justify-between">
                                <div>
                                    <h2 className="text-white font-semibold text-lg">{activeVideo.title}</h2>
                                    <p className="text-gray-400 text-sm">{activeVideo.description}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    {!isVideoComplete(activeVideo.id) && (
                                        <Button 
                                            variant="outline"
                                            className="rounded-full gap-2"
                                            onClick={() => markAsComplete(activeVideo.id)}
                                        >
                                            <CheckCircle className="h-4 w-4" />
                                            Mark as Complete
                                        </Button>
                                    )}
                                    <Button 
                                        variant="ghost"
                                        className="text-white"
                                        onClick={() => setActiveVideo(null)}
                                    >
                                        Close
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Course Modules */}
            <section className="py-12">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto space-y-8">
                        {courseModules.map((module, moduleIndex) => (
                            <motion.div
                                key={module.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: moduleIndex * 0.1 }}
                            >
                                <Card className="overflow-hidden">
                                    {/* Module Header */}
                                    <div className="p-6 bg-muted/30 border-b">
                                        <div className="flex items-center gap-4">
                                            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                                                <module.icon className="h-6 w-6 text-primary" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm text-muted-foreground">Module {module.id}</span>
                                                    <Badge variant="outline" className="text-xs">
                                                        {module.videos.length} {module.videos.length === 1 ? 'video' : 'videos'}
                                                    </Badge>
                                                </div>
                                                <h3 className="font-heading text-xl font-semibold">{module.title}</h3>
                                                <p className="text-sm text-muted-foreground">{module.description}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Videos List */}
                                    <div className="divide-y">
                                        {module.videos.map((video, videoIndex) => {
                                            const isComplete = isVideoComplete(video.id);
                                            const isActive = activeVideo?.id === video.id;
                                            
                                            return (
                                                <div 
                                                    key={video.id}
                                                    className={`p-4 flex items-center gap-4 hover:bg-muted/50 transition-colors cursor-pointer ${isActive ? 'bg-primary/5' : ''}`}
                                                    onClick={() => setActiveVideo(video)}
                                                    data-testid={`video-${video.id}`}
                                                >
                                                    {/* Thumbnail */}
                                                    <div className="relative flex-shrink-0">
                                                        <img 
                                                            src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
                                                            alt={video.title}
                                                            className="w-32 h-20 object-cover rounded-lg"
                                                        />
                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                            <div className="h-10 w-10 rounded-full bg-black/70 flex items-center justify-center">
                                                                <Play className="h-5 w-5 text-white ml-1" />
                                                            </div>
                                                        </div>
                                                        {isComplete && (
                                                            <div className="absolute top-1 right-1 h-5 w-5 rounded-full bg-green-500 flex items-center justify-center">
                                                                <CheckCircle className="h-3 w-3 text-white" />
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Info */}
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className={`font-medium ${isComplete ? 'text-muted-foreground' : ''}`}>
                                                            {video.title}
                                                        </h4>
                                                        <p className="text-sm text-muted-foreground truncate">
                                                            {video.description}
                                                        </p>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <Clock className="h-3 w-3 text-muted-foreground" />
                                                            <span className="text-xs text-muted-foreground">{video.duration}</span>
                                                            {isComplete && (
                                                                <Badge className="bg-green-100 text-green-700 text-xs">Completed</Badge>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Completion Section */}
            {completedCount === totalVideos && (
                <section className="py-12 bg-gradient-to-r from-green-50 to-emerald-50">
                    <div className="container mx-auto px-4 text-center">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                        >
                            <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                                <GraduationCap className="h-10 w-10 text-green-600" />
                            </div>
                            <h2 className="font-heading text-2xl font-bold text-green-800 mb-2">
                                Congratulations! 🎉
                            </h2>
                            <p className="text-green-700 mb-6">
                                You've completed all video classes! You're ready to ace your flight attendant interview.
                            </p>
                            <div className="flex items-center justify-center gap-4">
                                <Button 
                                    className="rounded-full"
                                    onClick={() => navigate('/coaching')}
                                >
                                    Book 1-on-1 Coaching
                                </Button>
                                <Button 
                                    variant="outline"
                                    className="rounded-full"
                                    onClick={() => navigate('/membership')}
                                >
                                    Join Membership
                                </Button>
                            </div>
                        </motion.div>
                    </div>
                </section>
            )}
        </div>
    );
}
